/**
 * Development environment.
 *
 * `serviceUrl` is the XMPP websocket the client connects through; muc and focus
 * hosts are derived from the domain by the adapter, so only the domain has to be
 * kept in step with the deployment.
 */
export const environment = {
  production: false,
  /**
   * DoctorConsole API. Carries the consultation record: appointment lookup,
   * session start/end and chat persistence. Empty disables every REST call —
   * the call itself still works, but nothing about it is recorded.
   */
  apiUrl: 'https://tvamdevtelemedvcon.azurewebsites.net/api',
  /**
   * Partner webhook — a different host from `apiUrl`, and sent no token. The
   * JOIN/LEAVE and media-toggle events go here. Empty disables them.
   */
  webhookUrl:
    'https://tvamcustomermicroservicev8.azurewebsites.net/ProcessConsultationWebhookV2',
  /**
   * Log shipping (§9.4). Empty until an endpoint is deployed: `apiUrl` has no
   * such route, so pointing it there would only post into a 404 every 15 s.
   */
  logShippingUrl: '',
  /**
   * Live captions (§8).
   *
   * `endpoint` empty disables the feature entirely — the provider reports
   * unavailable and the control is never offered, rather than being visible and
   * failing when pressed.
   *
   * The socket must be `wss://` in any deployment: the app is served over HTTPS,
   * so a `ws://` endpoint is blocked as mixed content, and in the Android
   * WebView that failure is silent (§8.8). `ws://localhost` is fine for desktop
   * development only (environment.local.ts).
   *
   * The dev translation server is the Azure App Service below, a relay to a
   * cloud engine; the key lives in its app settings (server/translation).
   *
   * `languages` is the fallback offered before a server has declared its own.
   * The authority is the engine — whether a language works is a fact about the
   * deployed model, so `capabilities()` replaces this list once a session has
   * connected (§8.3).
   */
  translation: {
    endpoint:
      'wss://tvamdev1translateservice-dve7dwfuesadgae3.southindia-01.azurewebsites.net/translate',
    languages: [
      { code: 'en-IN', label: 'English' },
      { code: 'hi-IN', label: 'हिन्दी' },
      { code: 'mr-IN', label: 'मराठी' },
      { code: 'kn-IN', label: 'ಕನ್ನಡ' },
      { code: 'ta-IN', label: 'தமிழ்' },
      { code: 'ml-IN', label: 'മലയാളം' },
    ],
    defaultLanguage: 'en-IN',
  },
  /**
   * Translated voice (§8.9). Uses `translation.endpoint`; which model runs is
   * the server's choice (`TRANSLATION_VOICE_ENGINE`), so it never appears here
   * — and neither does any API key, since this file is compiled into the browser bundle.
   */
  voiceTranslation: { enabled: true },
  jitsi: {
    domain: 'vcon.tvamapp.com',
    serviceUrl: 'wss://vcon.tvamapp.com/xmpp-websocket',
  },
  /**
   * TEST ONLY — send a recorded clip instead of the live mic whenever the local
   * participant speaks (voice-substitution.ts). Also requires
   * `?substituteAudio=1` on the URL, so an ordinary dev session is unaffected.
   */
  voiceSubstitution: {
    clips: {
      patient: 'assets/test-audio/audio1.mp3',
      doctor: 'assets/test-audio/audio2.mp3',
    },
  },
};
